import { yupResolver } from "@hookform/resolvers/yup";
import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import {
	Button,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Modalv2
} from "@itandi/itandi-bb-ui";
import { useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { DealClientTodoModalForm } from "~/components/common/deals/show/main/todos/DealClientTodoModalForm";
import { dealTodosCacheKey } from "~/hooks/deals/useDealTodos";
import { buildIndexPath } from "~/hooks/useCompanyDealTodos";
import type {
	CompanyDealTodo,
	CompanyDealTodoSearchCondition
} from "~/models/CompanyDealTodo";
import type { Deal } from "~/models/Deal";
import type { DealTodo, DealTodoEdit } from "~/models/DealTodo";
import { buildInitialDealTodoEdit, validationSchema } from "~/models/DealTodo";
import type { Staff } from "~/models/Staff";
import { isSuccess } from "~/utils/api/api_handler";
import { create, update } from "~/utils/api/deals/DealTodo";
import { onInvalidScroll } from "~/utils/FormUtil";

type Props = DeepReadonly<{
	deal: Deal | CompanyDealTodo["deal"];
	handleClose: () => void;
	staffs: Staff[];
	todo?: DealTodo | CompanyDealTodo | null;
}> & {
	searchCondition?: CompanyDealTodoSearchCondition;
};

export const DealClientTodoModal: React.FC<Props> = ({
	deal,
	staffs,
	handleClose,
	todo = null,
	searchCondition = {}
}: Props) => {
	const { mutate: globalMutate } = useSWRConfig();
	const methods = useForm<DealTodoEdit>({
		defaultValues: todo ?? buildInitialDealTodoEdit(),
		mode: "onChange",
		resolver: yupResolver(validationSchema),
		shouldFocusError: false
	});
	useEffect(() => {
		if (methods.watch("staff") === null) {
			methods.setValue("staff", deal.staff);
		}
	}, [todo, methods, deal]);
	const mutateTodo = useCallback(async (): Promise<void> => {
		await globalMutate(dealTodosCacheKey(deal.id));
		await globalMutate(buildIndexPath(searchCondition));
	}, [deal.id, globalMutate, searchCondition]);
	const handleModalSubmit = useCallback(() => {
		methods.handleSubmit(
			async (submitValue: DealTodoEdit) => {
				if (todo === null) {
					const response = await create(deal.id, submitValue);
					if (isSuccess(response)) {
						await mutateTodo();
						handleClose();
						toast.success("TODOを追加しました");
						return;
					}
					toast.error("エラーが発生しました");
					return;
				}
				const response = await update(deal.id, todo.id, submitValue);
				if (isSuccess(response)) {
					await mutateTodo();
					handleClose();
					toast.success("TODOを更新しました");
					return;
				}
				toast.error("エラーが発生しました");
			},
			(errors) => {
				onInvalidScroll(errors);
			}
		)();
	}, [deal.id, handleClose, methods, mutateTodo, todo]);
	if (deal === null) return null;
	return (
		<Modalv2 isOpen onClose={handleClose}>
			<ModalHeader title={todo ? "TODOを編集" : "TODOを追加"} />
			<ModalBody>
				<FormProvider {...methods}>
					<DealClientTodoModalForm deal={deal} staffs={staffs} />
				</FormProvider>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button onClick={handleClose} variant="secondary">
						保存せず閉じる
					</Button>
				}
				rightArea={
					<Button
						disabled={methods.formState.isSubmitting}
						onClick={handleModalSubmit}
						variant="primary"
					>
						保存
					</Button>
				}
			/>
		</Modalv2>
	);
};
